import React from 'react';
import TextAreaInput from '@components/system/FormField/TextAreaInput';
import './TicketComments.scss';
import {useTranslation} from 'react-i18next';
import {createTicketComment} from '@shared/services/WorkflowServices';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {Controller, SubmitHandler, useForm} from 'react-hook-form';
import {Button} from '@material-ui/core';
import {
  TICKETING_SYSTEM_COMMON_PERMISSIONS,
  TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER
} from '@shared/constants/Mapping';
import TicketActivityItem, {
  ITicketingSystemTicketActivityItemProps
} from '../TicketActivityItem/TicketActivityItem';
import useTicketingSystemPermission, {
  IAllPossibleTicketModules,
  IGroupPermission
} from '../../../../hooks/useTicketingSystemPermission';

interface ITicketCommentProps {
  clinicGroupId: number;
  clinicId: number;
  tickedId: number;
  selectedDataList: any;
  handleCommentAction(): void;
  module: IAllPossibleTicketModules;
  groupPermission?: IGroupPermission;
  renderTicketActivityItemContent?: ITicketingSystemTicketActivityItemProps['renderTicketActivityItemContent'];
}

interface ICreateCommentFields {
  comment: string;
}
const TicketComment = (props: ITicketCommentProps): JSX.Element => {
  const {t} = useTranslation();
  const {
    clinicGroupId,
    clinicId,
    tickedId,
    selectedDataList,
    handleCommentAction,
    groupPermission,
    module,
    renderTicketActivityItemContent
  } = props;
  const {handleSubmit, control, errors, reset} = useForm<ICreateCommentFields>({
    defaultValues: {
      comment: ''
    }
  });

  const {enqueueSnackbar} = useSnackbar();

  const hasCreateTicketCommentPermission = useTicketingSystemPermission({
    module,
    objectFunction: TICKETING_SYSTEM_COMMON_PERMISSIONS.CREATE_COMMENT,
    groupPermission
  });

  const createComment: SubmitHandler<ICreateCommentFields> = async values => {
    try {
      const res = await createTicketComment({
        clinicGroupId,
        clinicId,
        tickedId,
        payload: {
          text: values.comment,
          type: 'comment'
        },
        params: {
          module: TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[module]
        }
      });
      enqueueSnackbar(res.message, SnackBarConfig('s'));
      handleCommentAction();
      reset();
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    }
  };

  return (
    <section className="ticketing-system-ticket-comment-wrapper">
      {hasCreateTicketCommentPermission && (
        <form
          onSubmit={handleSubmit(createComment)}
          className="add-comment-form">
          <Controller
            control={control}
            name="comment"
            render={inputProps => (
              <TextAreaInput
                {...inputProps}
                placeholder={t('TICKETING_SYSTEM.ACTIVITY.COMMENT_PLACEHOLDER')}
                error={errors['comment']}
              />
            )}
          />
          <Button variant="contained" type="submit" color="primary">
            {t('COMMON.ADD')}
          </Button>
        </form>
      )}
      <div className="comments-wrapper">
        {selectedDataList?.data.map((item: any) => (
          <TicketActivityItem
            renderTicketActivityItemContent={renderTicketActivityItemContent}
            key={item.id}
            item={item}
          />
        ))}
      </div>
    </section>
  );
};

export default TicketComment;
